import { MOUNTS, screenDirToWorld, type PlayerCommand, type World, npcLifeOf } from '@nine/shared';
import { LockLook } from './ui/lockLook';
import { bindGroundAim, groundAim } from './groundAim';
import { settings } from './settings';
import { saveCameraPrefs } from './storage';
import { RightDrag, type CameraTurn } from './render/cameraTurn';
import type { Renderer } from './render/renderer';
import type { Hud } from './ui/hud';
import type { LockClient } from './ui/lockOn';
import type { PanelTab, Panels } from './ui/panels';

const PANEL_KEYS: Record<string, PanelTab> = {
  c: 'char',
  i: 'bag',
  k: 'arts',
  n: 'neigong',
  j: 'school',
  h: 'help',
  u: 'skin',
  y: 'mount',
  b: 'mall',
  p: 'pass',
  l: 'ladder',
  o: 'arena',
};
/** Zoom per pixel of trackpad scroll, in log scale: 100 px ≈ a step. */
const WHEEL_ZOOM = 0.0025;
const MOVE_KEYS: Record<string, [number, number]> = {
  w: [0, -1],
  arrowup: [0, -1],
  s: [0, 1],
  arrowdown: [0, 1],
  a: [-1, 0],
  arrowleft: [-1, 0],
  d: [1, 0],
  arrowright: [1, 0],
};

/**
 * What the input drives: the 2.5D renderer, or the 3D view (render3d/core/view.ts) while it is on,
 * which hands every call on to the 2.5D renderer whenever that one is shown (indoors, cutscenes).
 */
export type InputView = Pick<
  Renderer,
  | 'hoverClient'
  | 'aim'
  | 'clickMark'
  | 'zoom'
  | 'onZoomSettle'
  | 'zoomBy'
  | 'zoomScale'
  | 'zoomHold'
  | 'rotate'
  | 'screenToWorld'
  | 'pick'
  | 'iso'
> & {
  readonly turn: { readonly control: CameraTurn };
  /** A right-drag up or down (CSS pixels): tilts a camera that tilts. */
  tilt?(dy: number): void;
  /** The camera's pitch, held by PageUp / PageDown (C0-ROTATE). */
  readonly pitch?: { hold(dir: number): void };
  /** The right button's guard-or-drag, if the view has its own (`RightDrag` otherwise). */
  newRightDrag?(): RightDragLike;
};

/** The right mouse button: a guard at once, a camera move once dragged (render/cameraTurn.ts). */
interface RightDragLike {
  readonly phase: 'idle' | 'guard' | 'turn';
  press(x: number, y: number): void;
  move(x: number, y: number): { started: boolean; dx: number; dy?: number };
  release(): 'idle' | 'guard' | 'turn';
  /** The guard the press raised stays up through a camera turn (render/cameraTurn.ts). */
  readonly keepsGuard?: boolean;
}

/** Maps keyboard, mouse, touch and gamepad to world commands. */
export class Input {
  enabled = false;
  /** Esc handler (closes windows, else opens the HUD's menu); set by main. */
  onEscape: (() => void) | null = null;
  /** The persistent lock-on (ui/lockOn.ts): Tab, a click, and A/D circling the locked target. */
  lock: LockClient | null = null;
  /** M: the big map. */
  onMap: (() => void) | null = null;
  /** G: the emote picker (ท่าโพส · อีโมจิ). */
  onEmote: (() => void) | null = null;
  /** 9: the professions window (อาชีพ). */
  onTrades: (() => void) | null = null;
  onToday: (() => void) | null = null;
  onFame: (() => void) | null = null;
  /** E at a resource node: work it (true), or nothing there to work (false: talk instead). */
  onWork: (() => boolean) | null = null;
  private held = new Set<string>();
  private lastTap = { key: '', at: 0 };
  /** Ctrl pressed alone (no direction held): slides the way the player faces when it comes up without another key. */
  private ctrlTap: { at: number; clean: boolean } | null = null;
  private lastDir: [number, number] = [0, 0];
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private pointerType = 'mouse';
  private mouseBlock = false;
  private keyBlock = false;
  private touchBlock = false;
  private padBlock = false;
  /** Gamepad stick / d-pad heading in screen space (unit vector, or 0,0). */
  private padDir: [number, number] = [0, 0];
  /** The right mouse button: a guard, or a drag turning the camera. */
  private readonly rightDrag: RightDragLike;
  /** Locked and the right button held: face the camera's way (ui/lockLook.ts). */
  private readonly look: LockLook;

  constructor(
    private readonly view: HTMLCanvasElement,
    private readonly world: World,
    private readonly renderer: InputView,
    private readonly hud: Hud,
    private readonly panels: Panels,
    private readonly send: (c: PlayerCommand) => void,
    private readonly onGesture: () => void,
  ) {
    this.rightDrag = renderer.newRightDrag?.() ?? new RightDrag();
    this.look = new LockLook(world, (x, y) => renderer.iso.dirToMap(x, y), (c) => this.enabled && send(c));
    bindGroundAim({ world, view: renderer, send: (c) => this.enabled && send(c) });
    addEventListener('keydown', this.onKeyDown);
    addEventListener('keyup', this.onKeyUp);
    addEventListener('blur', this.releaseAll);
    view.addEventListener('pointerdown', this.onPointerDown);
    view.addEventListener('pointermove', this.onPointerMove);
    view.addEventListener('pointerup', this.onPointerUp);
    view.addEventListener('pointercancel', this.onPointerUp);
    view.addEventListener('pointerleave', () => (this.renderer.hoverClient = null));
    view.addEventListener('contextmenu', (e) => e.preventDefault());
    view.addEventListener('wheel', this.onWheel, { passive: false });
    // Saved once a zoom or a turn comes to rest, not every frame of it.
    renderer.onZoomSettle = () => this.saveCamera();
    renderer.turn.control.onSettle = () => this.saveCamera();
  }

  /** Zooms in (+1) or out (−1) by a step, animated (buttons, wheel notches). */
  zoomCamera(step: number): void {
    this.renderer.zoomBy(step);
  }

  private saveCamera(): void {
    const angle = this.renderer.turn.control.angle;
    const rot = ((Math.round(angle) % 4) + 4) % 4;
    saveCameraPrefs({ zoom: this.renderer.zoom, rot, angle: rot + (angle - Math.round(angle)) });
  }

  /**
   * Turns the camera one rest angle (a quarter turn) as a drag would: +1 swings the world the way a
   * drag to the right does.
   */
  turnCamera(dir: number): void {
    this.renderer.rotate(-dir);
  }

  /** Camera-turn keys held down ([ and Ctrl+← are 'l', ] and Ctrl+→ are 'r'). */
  private turnKeys = new Set<'l' | 'r'>();
  /** The gamepad's right stick sideways (−1..1). */
  private padTurn = 0;

  private setKeyTurn(side: 'l' | 'r', down: boolean): void {
    if (down) this.turnKeys.add(side);
    else if (!this.turnKeys.delete(side)) return;
    this.syncTurn();
  }

  /** Keys and stick together: held, the camera eases up to turning speed; let go, it eases to a stop. */
  private syncTurn(): void {
    const keys = (this.turnKeys.has('r') ? 1 : 0) - (this.turnKeys.has('l') ? 1 : 0);
    const dir = Math.max(-1, Math.min(1, keys + this.padTurn));
    this.renderer.turn.control.hold(-dir);
  }

  /**
   * The camera turned (render/turnView.ts onAngle): keys and the stick are screen directions, so a
   * walk in progress is sent again under the new angle (W stays up the screen while the view turns).
   */
  cameraTurned(): void {
    this.look.sync(this.rightDrag.phase !== 'idle');
    if (this.enabled && (this.held.size || this.padDir[0] || this.padDir[1])) this.syncMove();
  }

  /** The gamepad's right stick held sideways (−1..1, right as a drag to the right), or let go (0). */
  setPadTurn(dir: number): void {
    this.padTurn = dir;
    this.syncTurn();
  }

  /** Zoom held by the + / − keys and by the gamepad's right stick (+1 in, −1 out, 0). */
  private zoomKeys = 0;
  private zoomPad = 0;

  /** The gamepad's right stick held up (+1, zoom in) or down (−1), or let go (0). */
  setPadZoom(dir: number): void {
    this.zoomPad = dir;
    this.renderer.zoomHold(this.zoomKeys + this.zoomPad);
  }

  private setKeyZoom(dir: number): void {
    this.zoomKeys = dir;
    this.renderer.zoomHold(this.zoomKeys + this.zoomPad);
  }

  /** Time of the last wheel event (ms, event clock). */
  private wheelAt = -1e9;

  /**
   * Wheel / trackpad: vertical scroll or pinch zooms (the camera turns by right-drag and keys).
   * - A mouse wheel notch (the first event after a pause, or a line/page delta) is a step, animated,
   *   however small its delta (macOS mouse ticks can be ~4 px, Windows ones 100+).
   * - The rest of a scroll gesture (trackpad scrolling and its momentum, a fast-spun wheel) zooms in
   *   proportion to the scroll, about a step per 100 px, animated at the zoom's steady rate.
   * - A trackpad pinch (ctrl+wheel) follows the fingers at once: Chrome reports it as a scale of
   *   exp(−deltaY / 100).
   */
  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1;
    const dy = e.deltaY * unit;
    if (Math.abs(e.deltaX * unit) > Math.abs(dy) || !dy) return;
    const now = e.timeStamp;
    const fresh = now - this.wheelAt > 140;
    this.wheelAt = now;
    if (e.ctrlKey) return this.renderer.zoomScale(Math.exp(-dy / 100), true);
    if (fresh || e.deltaMode !== 0) this.zoomCamera(dy < 0 ? 1 : -1);
    else this.renderer.zoomScale(Math.exp(-dy * WHEEL_ZOOM));
  };

  /** Touches on the game view, for two-finger pinch zoom. */
  private touches = new Map<number, [number, number]>();
  /** Finger spread at the last pinch move, or 0 when not pinching. */
  private pinchFrom = 0;

  private touchSpread(): number {
    const [a, b] = [...this.touches.values()];
    return a && b ? Math.hypot(a[0] - b[0], a[1] - b[1]) : 0;
  }

  /** Two fingers on the view pinch to zoom, the scale following the change in their spread. */
  private onTouchMove(e: PointerEvent): boolean {
    if (!this.touches.has(e.pointerId)) return false;
    this.touches.set(e.pointerId, [e.clientX, e.clientY]);
    if (!this.pinchFrom) return false;
    const spread = this.touchSpread();
    if (spread > 0) {
      this.renderer.zoomScale(spread / this.pinchFrom, true);
      this.pinchFrom = spread;
    }
    return true;
  }

  /** Called by the hotbar's 守 button (touch). */
  setTouchBlock(on: boolean): void {
    this.touchBlock = on;
    this.syncBlock();
  }

  dash(): void {
    const p = this.world.player;
    const hover = this.renderer.hoverClient;
    if (this.pointerType === 'mouse' && hover) {
      const [tx, ty] = this.renderer.screenToWorld(...hover);
      if (Number.isFinite(tx) && Number.isFinite(ty))
        return this.send({ t: 'dash', dx: tx - p.x, dy: ty - p.y });
    }
    this.send({ t: 'dash', dx: p.dirX, dy: p.dirY });
  }

  /**
   * Uses the move in a hotbar slot. A ground-targeted move first shows its circle under the mouse;
   * a click (or the same key again) casts it there, right-click or Esc cancels; with the
   * `quickCastGround` setting the press casts at the mouse at once. On touch a tap lands on the
   * target or ahead (a drag from the button aims: groundAim.ts).
   */
  useSlot(slot: number): void {
    const s = this.world.activeSkills()[slot];
    if (s?.ground && this.pointerType === 'mouse') {
      groundAim.at = null;
      if (this.renderer.aim?.slot === slot) return this.castAimed();
      this.renderer.aim = { slot, range: s.range, radius: s.aoe ?? 1 };
      if (settings.quickCastGround && this.renderer.hoverClient) this.castAimed();
      return;
    }
    this.renderer.aim = null;
    this.send({ t: 'skill', slot });
  }

  private castAimed(): void {
    const aim = this.renderer.aim;
    const hover = this.renderer.hoverClient;
    this.renderer.aim = null;
    if (!aim || !hover) return;
    const [x, y] = this.renderer.screenToWorld(...hover);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    this.send({ t: 'skill', slot: aim.slot, at: { x, y } });
  }

  /** Gamepad: held guard trigger. */
  setPadBlock(on: boolean): void {
    this.padBlock = on;
    this.syncBlock();
  }

  /** Gamepad: walking heading in screen space (x right, y down); 0,0 stops. */
  setPadDir(sx: number, sy: number): void {
    this.padDir = [sx, sy];
    this.syncMove();
  }

  /** Space: on a flying mount climbs, otherwise jumps. `down` dives instead. */
  jumpOrClimb(down = false): void {
    const p = this.world.player;
    if (p.mount && MOUNTS[p.mount]?.fly) this.send({ t: 'fly', dir: down ? -1 : 1 });
    else this.send({ t: 'jump' });
  }

  /** Q: on a flying mount dives, otherwise dashes. */
  /** Slides along the held direction keys; false (nothing sent) when none is held. */
  private slideHeld(): boolean {
    let sx = 0;
    let sy = 0;
    for (const k of this.held) {
      const d = MOVE_KEYS[k];
      if (d) {
        sx += d[0];
        sy += d[1];
      }
    }
    if (!sx && !sy) return false;
    const [dx, dy] = this.renderer.iso.dirToMap(...screenDirToWorld(sx, sy));
    this.send({ t: 'slide', dx, dy });
    return true;
  }

  dashOrDive(): void {
    const p = this.world.player;
    if (p.mount && MOUNTS[p.mount]?.fly) this.send({ t: 'fly', dir: -1 });
    else this.dash();
  }

  /** E: talk to the nearest NPC within reach (or take the door or stairs at hand). */
  talkNearest(): void {
    if (this.onWork?.()) return;
    const p = this.world.player;
    // The person picked out first, if they are near enough to call to.
    const sel = this.world.get(p.targetId);
    if (
      sel &&
      !sel.dead &&
      (sel.kind === 'npc' || (sel.kind === 'ambient' && (sel.folk || npcLifeOf(this.world)?.mindOf(sel)))) &&
      Math.hypot(sel.x - p.x, sel.y - p.y) < 8
    )
      return this.send({ t: 'interact', id: sel.id });
    // A door or the stairs within reach: in, out, up or down (interiors, in the shared rules).
    const door = this.world.interiors.nearestUse();
    if (door) return this.send(door);
    let best: { id: number; d: number } | null = null;
    for (const n of this.world.entities.values()) {
      if (n.kind !== 'npc') continue;
      const d = Math.hypot(n.x - p.x, n.y - p.y);
      if (d < 8 && (!best || d < best.d)) best = { id: n.id, d };
    }
    if (best) this.send({ t: 'interact', id: best.id });
  }

  /** Esc: drop the aim, else the main handler (close windows / the menu). */
  escape(): void {
    this.lock?.off();
    if (this.renderer.aim) this.renderer.aim = null;
    else if (this.onEscape) this.onEscape();
    else {
      this.hud.closeDialog();
      this.panels.close();
    }
  }

  private syncBlock(): void {
    this.send({ t: 'block', on: this.mouseBlock || this.keyBlock || this.touchBlock || this.padBlock });
  }

  /** Re-reads the keys (a locked target moves, so the circle's direction does). */
  refreshMove(): void {
    if (this.enabled && this.lock && this.lock.id >= 0 && this.held.size) this.syncMove();
  }

  private syncMove(): void {
    let [sx, sy] = this.padDir;
    for (const k of this.held) {
      const d = MOVE_KEYS[k];
      if (d) {
        sx += d[0];
        sy += d[1];
      }
    }
    // (Locked on a target the keys stay screen-relative as in ordinary walking: only the facing is the target's.)
    const [dx, dy] = this.renderer.iso.dirToMap(...screenDirToWorld(sx, sy));
    if (dx !== this.lastDir[0] || dy !== this.lastDir[1]) {
      this.lastDir = [dx, dy];
      this.send({ t: 'moveDir', dx, dy });
    }
  }

  private releaseAll = (): void => {
    this.held.clear();
    this.zoomKeys = this.zoomPad = 0;
    this.renderer.zoomHold(0);
    if (this.enabled) this.send({ t: 'sprint', on: false });
    this.mouseBlock = this.keyBlock = this.touchBlock = this.padBlock = false;
    if (this.rightDrag.release() === 'turn') this.renderer.turn.control.release();
    this.look.sync(false);
    this.turnKeys.clear();
    this.padTurn = 0;
    this.renderer.pitch?.hold(0);
    this.renderer.turn.control.hold(0);
    this.padDir = [0, 0];
    if (this.enabled) {
      this.syncMove();
      this.syncBlock();
    }
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if ((e.target as HTMLElement | null)?.closest?.('input, textarea, select')) return;
    const k = e.key.toLowerCase();
    if (k === 'escape') return this.escape();
    if (!this.enabled) return;
    if (k === ' ' || k === 'tab' || k.startsWith('arrow')) e.preventDefault();
    if (e.repeat && !MOVE_KEYS[k]) return;
    this.onGesture();

    if (k === 'shift') {
      this.send({ t: 'sprint', on: true });
      return;
    }
    // สไลด์: hold a direction then press Ctrl (direction first: Ctrl+W would close the tab), or tap Ctrl alone.
    if (k === 'control') {
      if (this.slideHeld()) this.ctrlTap = null;
      else this.ctrlTap = { at: performance.now(), clean: true };
      return;
    }
    if (this.ctrlTap) this.ctrlTap.clean = false;
    // Turning the camera: Ctrl+← / → (Q and E are taken: dash and talk), or [ and ].
    const turn =
      (e.ctrlKey && (k === 'arrowleft' || k === 'arrowright')) ||
      e.code === 'BracketLeft' ||
      e.code === 'BracketRight';
    // Tilting the camera: PageUp toward the painted 30° (from above), PageDown flatter.
    if (e.code === 'PageUp' || e.code === 'PageDown') {
      e.preventDefault();
      if (!e.repeat) this.renderer.pitch?.hold(e.code === 'PageUp' ? 1 : -1);
      return;
    }
    if (turn) {
      e.preventDefault();
      if (!e.repeat) this.setKeyTurn(e.code === 'BracketRight' || k === 'arrowright' ? 'r' : 'l', true);
      return;
    }
    if (MOVE_KEYS[k]) {
      // Double-tapping a direction in mid-air dashes that way.
      const now = performance.now();
      const tapped = this.lastTap.key === k && now - this.lastTap.at < 260;
      this.lastTap = { key: k, at: now };
      this.held.add(k);
      this.syncMove();
      if (tapped && this.world.player.z > 0) {
        const [sx, sy] = MOVE_KEYS[k]!;
        const [dx, dy] = this.renderer.iso.dirToMap(...screenDirToWorld(sx, sy));
        this.send({ t: 'dash', dx, dy });
      }
    } else if (e.code === 'Equal' || e.code === 'NumpadAdd') this.setKeyZoom(1);
    else if (e.code === 'Minus' || e.code === 'NumpadSubtract') this.setKeyZoom(-1);
    else if (k >= '1' && k <= '8') this.useSlot(Number(k) - 1);
    // On a flying mount, holding Space climbs (Shift+Space dives) and holding Q dives.
    else if (k === ' ') this.jumpOrClimb(e.shiftKey);
    else if (k === 'q') this.dashOrDive();
    else if (k === 'f') {
      this.keyBlock = true;
      this.syncBlock();
    } else if (k === 'tab') {
      if (this.lock) this.lock.next();
      else this.send({ t: 'targetNearest' });
    }
    else if (k === 'z') this.send({ t: 'sheathe' });
    else if (k === 'v') this.send({ t: 'meditate' });
    else if (k === 'x') this.send({ t: 'mount' });
    else if (k === 'r') this.hud.basicAttack();
    else if (k === 't') this.hud.toggleAuto();
    else if (k === 'e') this.talkNearest();
    else if (k === 'm') this.onMap?.();
    else if (k === 'g') this.onEmote?.();
    else if (k === '9') this.onTrades?.();
    else if (k === '0') this.onToday?.();
    else if (k === ';') this.onFame?.();
    else if (PANEL_KEYS[k]) this.panels.toggle(PANEL_KEYS[k]);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    const k = e.key.toLowerCase();
    if (k === 'control' && this.ctrlTap) {
      const tap = this.ctrlTap;
      this.ctrlTap = null;
      if (tap.clean && this.enabled && performance.now() - tap.at < 300) this.send({ t: 'slide', dx: 0, dy: 0 });
    }
    if (e.code === 'PageUp' || e.code === 'PageDown') this.renderer.pitch?.hold(0);
    // A held camera turn ends when its key (or Ctrl) comes up.
    if (e.code === 'BracketLeft' || k === 'arrowleft') this.setKeyTurn('l', false);
    if (e.code === 'BracketRight' || k === 'arrowright') this.setKeyTurn('r', false);
    if (k === 'control' && this.turnKeys.size) {
      this.turnKeys.clear();
      this.syncTurn();
    }
    const zoomIn = e.code === 'Equal' || e.code === 'NumpadAdd';
    const zoomOut = e.code === 'Minus' || e.code === 'NumpadSubtract';
    if ((zoomIn && this.zoomKeys > 0) || (zoomOut && this.zoomKeys < 0)) this.setKeyZoom(0);
    if (k === 'shift') {
      if (this.enabled) this.send({ t: 'sprint', on: false });
      return;
    }
    if (MOVE_KEYS[k]) {
      this.held.delete(k);
      if (!this.enabled) return;
      // Letting go of a diagonal rarely lifts both keys on the same frame. Wait a moment before
      // narrowing to the remaining key, so the character keeps its diagonal heading when it stops.
      const stillMoving = [...this.held].some((h) => MOVE_KEYS[h]);
      if (this.releaseTimer) clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
      if (stillMoving)
        this.releaseTimer = setTimeout(() => {
          this.releaseTimer = null;
          if (this.enabled) this.syncMove();
        }, 70);
      else this.syncMove();
    } else if (k === 'f') {
      this.keyBlock = false;
      if (this.enabled) this.syncBlock();
    } else if ((k === ' ' || k === 'q') && this.enabled) this.send({ t: 'fly', dir: 0 });
  };

  private onPointerMove = (e: PointerEvent): void => {
    this.pointerType = e.pointerType;
    if (e.pointerType === 'touch' && this.onTouchMove(e)) return;
    if (this.rightDrag.phase !== 'idle') {
      // Right button held: dragged sideways it turns the camera; the guard stays up (`keepsGuard`, user 10-11).
      const { started, dx, dy } = this.rightDrag.move(e.clientX, e.clientY);
      if (started) {
        this.renderer.turn.control.grab();
        this.view.style.cursor = 'grabbing';
        if (this.mouseBlock && !this.rightDrag.keepsGuard) {
          this.mouseBlock = false;
          if (this.enabled) this.syncBlock();
        }
      }
      if (dx) this.renderer.turn.control.drag(dx);
      if (dy) this.renderer.tilt?.(dy);
      this.look.sync(true);
      if (this.rightDrag.phase === 'turn') return;
    }
    this.renderer.hoverClient = [e.clientX, e.clientY];
    this.view.style.cursor = this.renderer.pick(e.clientX, e.clientY) ? 'pointer' : 'default';
  };

  private onPointerDown = (e: PointerEvent): void => {
    this.pointerType = e.pointerType;
    if (e.pointerType === 'touch') {
      this.touches.set(e.pointerId, [e.clientX, e.clientY]);
      // A second finger starts a pinch instead of another tap.
      if (this.touches.size >= 2) {
        this.pinchFrom = this.touchSpread();
        return;
      }
    }
    this.renderer.hoverClient = [e.clientX, e.clientY];
    if (!this.enabled) return;
    this.onGesture();
    if (this.renderer.aim) {
      if (e.button === 0) this.castAimed();
      else if (e.button === 2) this.renderer.aim = null;
      return;
    }
    if (e.button === 2) {
      this.view.setPointerCapture(e.pointerId);
      // Guard at once, as always; a sideways drag turns it into a camera turn (onPointerMove).
      if (e.pointerType === 'mouse') this.rightDrag.press(e.clientX, e.clientY);
      this.mouseBlock = true;
      this.syncBlock();
      this.look.sync(true);
      return;
    }
    if (e.button !== 0) return;
    const hit = this.renderer.pick(e.clientX, e.clientY);
    if (hit?.kind === 'monster') {
      this.lock?.set(hit.id);
      return this.send({ t: 'engage', id: hit.id });
    }
    // Another real player (a server's): pick them out, their frame shows (right-click its face for the menu).
    if (hit?.kind === 'player' && hit.id !== this.world.player.id) {
      this.lock?.set(hit.id);
      return this.send({ t: 'select', id: hit.id });
    }
    if (hit?.kind === 'rival') {
      // Another player: fight them if PK allows it, otherwise just walk on.
      if (this.world.isHostile(this.world.player, hit)) return this.send({ t: 'engage', id: hit.id });
      // Otherwise pick them, to see their blood or ask them into the party.
      if (!this.world.fellowOf(hit) && !this.world.player.pk)
        this.hud.toast('เปิด PK (ปุ่ม PK มุมขวาล่าง) เพื่อโจมตีผู้เล่นอื่น · ต่อสู้ในลานศิลาไม่ได้');
      return this.send({ t: 'select', id: hit.id });
    }
    // People: the first click picks them out (their frame shows and the player looks at them); a
    // click on the one already picked (or E) talks to them.
    if (hit && (hit.kind === 'npc' || (hit.kind === 'ambient' && hit.folk)))
      return this.send(
        this.world.player.targetId === hit.id ? { t: 'interact', id: hit.id } : { t: 'select', id: hit.id },
      );
    // The plains' townsfolk (shared systems/npcLife.ts): picked by the ground under the pointer, as people are.
    if (!hit) {
      const [gx, gy] = this.renderer.screenToWorld(e.clientX, e.clientY);
      const life = Number.isFinite(gx) && Number.isFinite(gy) ? npcLifeOf(this.world)?.personAt(gx, gy) : undefined;
      if (life)
        return this.send(
          this.world.player.targetId === life.id ? { t: 'interact', id: life.id } : { t: 'select', id: life.id },
        );
    }
    // A click on empty ground lets go of the lock and the target (unless it is a foe still being fought).
    this.lock?.off();
    const cur = this.world.get(this.world.player.targetId);
    if (cur && !(this.world.isHostile(this.world.player, cur) && this.world.player.combatT > 0))
      this.send({ t: 'untarget' });
    const [x, y] = this.renderer.screenToWorld(e.clientX, e.clientY);
    // No ground under the pointer (a view with no size, a ray along the ground): no walk.
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    this.send({ t: 'moveTo', x, y });
    // The ring lies on the ground, drawn with the ground layer (in the painted view's pixels).
    const [sx, sy] = this.renderer.iso.projView(x, y);
    this.renderer.clickMark = { x: sx, y: sy, t: this.world.time };
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (this.touches.delete(e.pointerId) && this.touches.size < 2) this.pinchFrom = 0;
    if (e.button === 2 && this.rightDrag.release() === 'turn') {
      // Let go of a turn: it eases on to the nearest angle the world is painted for.
      this.renderer.turn.control.release();
      this.view.style.cursor = 'default';
    }
    if (e.button === 2) this.look.sync(false);
    if (e.button === 2 && this.mouseBlock) {
      this.mouseBlock = false;
      if (this.enabled) this.syncBlock();
    }
  };
}
